import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { listExchangeLog, pushHistoryToSocson } from '../api/history';
import { listUsers } from '../api/users';
import { chatUsers } from '../api/chat';
import { fmtDateTime, roleName } from '../utils/format';

// Nhãn nguồn (đồng bộ với panel trao đổi trong Quản lý KH).
// Màu viền trái theo thiết kế: CSKH xanh · Ghi chú trực tiếp chàm · Công việc hổ phách.
const SRC = {
  note: { label: '💬 Ghi chú trực tiếp', color: 'var(--accent)', border: 'var(--accent)' },
  task: { label: '📋 Công việc', color: 'var(--warn)', border: 'var(--warn-fill)' },
  cskh: { label: '🎧 CSKH', color: 'var(--ok)', border: 'var(--ok)' },
};
const SRC_TABS = [
  { key: '', label: 'Tất cả' },
  { key: 'note', label: '💬 Ghi chú trực tiếp' },
  { key: 'task', label: '📋 Công việc' },
  { key: 'cskh', label: '🎧 CSKH' },
];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function ExchangeLogPage() {
  const { me } = useAuth();
  // TP chỉ xem note nhân viên phòng mình, KHÔNG thấy log CSKH (backend chặn) -> ẩn luôn tab CSKH.
  const seesCskh = me.role === 'GD' || me.role === 'CV' || (me.departments || []).includes('Phòng CSKH') || (me.deptHeadOf || []).includes('Phòng CSKH');
  const srcTabs = seesCskh ? SRC_TABS : SRC_TABS.filter((t) => t.key !== 'cskh');
  const toast = useToast();
  const [pushing, setPushing] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState(ymd(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(ymd(new Date()));
  const [users, setUsers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // /users chỉ cho GĐ -> TP dùng /chat/users (ai đăng nhập cũng gọi được, đủ id + tên).
    listUsers().then(setUsers).catch(() => chatUsers().then(setUsers).catch(() => {}));
  }, []);

  function load(page = 1, append = false) {
    setLoading(true);
    listExchangeLog({ search: search || undefined, userId: userId || undefined, source: source || undefined, from, to, page, limit: 30 })
      .then((d) => setData((prev) => (append ? { ...d, items: [...prev.items, ...d.items] } : d)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load(1, false);
  }, [search, userId, source, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doPush(h) {
    setPushing(h._id);
    try {
      await pushHistoryToSocson(h._id);
      setData((prev) => ({ ...prev, items: prev.items.map((x) => (x._id === h._id ? { ...x, socsonPushed: true, socsonPushError: '' } : x)) }));
      toast('Đã đẩy lên SocSon');
    } catch (err) {
      const msg = err.response?.data?.message || 'Lỗi đẩy lên SocSon';
      // Lưu lỗi vào item để hiển thị trạng thái "đẩy thất bại" ngay tại dòng.
      setData((prev) => ({ ...prev, items: prev.items.map((x) => (x._id === h._id ? { ...x, socsonPushError: msg.replace(/^Đẩy lên SocSon thất bại:\s*/, '') } : x)) }));
      toast(msg, 'danger');
    } finally {
      setPushing('');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2>🗒️ Nhật ký trao đổi nội bộ</h2>
          <div className="sub">Toàn bộ ghi chú của mọi nhân viên với khách hàng — gộp 3 nguồn.</div>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input
            style={{ flex: 1, minWidth: 200 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput.trim())}
            placeholder="Tìm theo mã KH, tên KH, nội dung…"
          />
          <button className="btn sm" onClick={() => setSearch(searchInput.trim())}>Tìm</button>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Tất cả nhân viên</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}{u.employeeCode ? ` (${u.employeeCode})` : ''}</option>
            ))}
          </select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 'auto' }} onClick={(e) => e.currentTarget.showPicker?.()} />
          <span className="small muted">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 'auto' }} onClick={(e) => e.currentTarget.showPicker?.()} />
        </div>

        <div className="seg" style={{ marginBottom: 12, flexWrap: 'wrap', maxWidth: '100%' }}>
          {srcTabs.map((t) => (
            <button key={t.key} className={source === t.key ? 'active' : ''} onClick={() => setSource(t.key)}>{t.label}</button>
          ))}
        </div>

        <div className="small muted" style={{ marginBottom: 8 }}>
          {data.total} trao đổi · {from.split('-').reverse().join('/')} → {to.split('-').reverse().join('/')}
        </div>

        {loading && !data.items.length ? (
          <div className="empty">Đang tải…</div>
        ) : !data.items.length ? (
          <div className="empty">Không có trao đổi nào khớp bộ lọc.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.items.map((h) => {
                const s = SRC[h.source] || SRC.note;
                return (
                  <div key={h._id} className="card" style={{ background: 'var(--panel2)', margin: 0, borderLeft: `3px solid ${s.border}` }}>
                    <div className="row between" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <b className="small">{h.by?.name || '—'}</b>
                        {h.by?.role && <span className="small muted">{roleName(h.by.role)}</span>}
                        <span className="tag" style={{ color: s.color }}>{s.label}</span>
                        {(h.custCode || h.custName) && (
                          <span className="small" style={{ color: 'var(--accent)' }}>· {h.custCode} {h.custName}</span>
                        )}
                      </div>
                      <span className="small muted">{fmtDateTime(h.at)}</span>
                    </div>
                    {h.source === 'task' && h.taskTitle && (
                      <div className="small muted" style={{ margin: '2px 0 2px' }}>Công việc: {h.taskTitle}</div>
                    )}
                    <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', marginTop: 2 }}>{h.note}</div>
                    <div className="row" style={{ marginTop: 6, alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {h.socsonPushed ? (
                        <span className="chip done" style={{ fontSize: 11 }}>✓ Đã lên SocSon</span>
                      ) : h.socsonPushError ? (
                        <>
                          <span className="chip" style={{ fontSize: 11, background: 'color-mix(in srgb, var(--danger) 13%, transparent)', color: 'var(--danger)' }} title={h.socsonPushError}>
                            ✕ Đẩy thất bại: {h.socsonPushError}
                          </span>
                          <button className="btn sm ghost" disabled={pushing === h._id} onClick={() => doPush(h)} title="Thử đẩy lại lên SocSon">
                            {pushing === h._id ? 'Đang đẩy…' : '↻ Thử lại'}
                          </button>
                        </>
                      ) : (
                        <button className="btn sm ghost" disabled={pushing === h._id} onClick={() => doPush(h)} title="Đẩy ghi chú này lên màn Trao đổi SocSon">
                          {pushing === h._id ? 'Đang đẩy…' : '↑ Đẩy lên SocSon'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {data.page < data.totalPages && (
              <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
                <button className="btn ghost" disabled={loading} onClick={() => load(data.page + 1, true)}>
                  {loading ? 'Đang tải…' : 'Xem thêm'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
